import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { AppTopBar } from '@/components/AppTopBar';
import type { TripOption } from '@/components/TripSwitcher';
import { RoamAppProviders } from '@/components/roam-app/RoamAppProviders';
import { useUser } from '@/lib/auth/client-auth';
import { useQuery } from '@/lib/data/convex-bypass';
import { isDevAuthBypassEnabled } from '@/lib/dev/auth-bypass-flag';
import { api } from '@convex/_generated/api';

function hasConfiguredEnv(value: string | undefined): value is string {
  const trimmed = value?.trim();
  return Boolean(trimmed && !trimmed.includes('...') && !trimmed.includes('<'));
}

function LegalSignedInNavInner({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const trips = useQuery(api.trips.listMine, isDevAuthBypassEnabled ? 'skip' : {});
  const navState = useQuery(api.users.getNavState, isDevAuthBypassEnabled ? 'skip' : {});

  const tripOptions = useMemo<TripOption[]>(() => {
    return [...(trips ?? [])]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map((trip) => ({
        id: trip._id,
        name: trip.title,
        startDate: trip.startDate ?? null,
        endDate: trip.endDate ?? null,
        targetMonth: trip.targetMonth ?? null,
        status: trip.status,
        destinations: trip.destination ? [trip.destination] : [],
      }));
  }, [trips]);

  const email =
    user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses[0]?.emailAddress ?? 'Account';

  return (
    <>
      <AppTopBar
        email={email}
        trips={tripOptions}
        lastActiveTripId={navState?.lastActiveTripId ?? null}
        showTripSwitcher={false}
      />
      <div className="min-h-dvh [--app-nav-clear:4rem] md:[--app-nav-clear:4rem]">{children}</div>
    </>
  );
}

/** Keeps the standard app menu on legal pages for signed-in readers. */
export function LegalSignedInNav({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useUser();
  const convexUrl = process.env.NEXT_PUBLIC_CONVEX_URL;

  if (!isLoaded || !isSignedIn) {
    return <>{children}</>;
  }

  if (!hasConfiguredEnv(convexUrl)) {
    return <>{children}</>;
  }

  return (
    <RoamAppProviders convexUrl={convexUrl}>
      <LegalSignedInNavInner>{children}</LegalSignedInNavInner>
    </RoamAppProviders>
  );
}
